export type FieldChangeKind = "ADDED" | "REMOVED" | "CHANGED"

export type FieldChange = {
  /** Dotted path, e.g. `storeSettings.integrationStore`. */
  path: string
  kind: FieldChangeKind
  before?: unknown
  after?: unknown
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

/**
 * Field-level difference between a sampled document and its preview. Objects are walked so a
 * nested change names its own path; arrays are compared whole — an index-by-index diff of a
 * channel list reads worse than "this list became that list".
 */
export function diffDocument(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  prefix = ""
): FieldChange[] {
  const keys = Object.keys(before).concat(
    Object.keys(after).filter((key) => !(key in before))
  )
  return keys.flatMap((key): FieldChange[] => {
    const path = prefix ? `${prefix}.${key}` : key
    const inBefore = Object.prototype.hasOwnProperty.call(before, key)
    const inAfter = Object.prototype.hasOwnProperty.call(after, key)
    const from = before[key]
    const to = after[key]
    if (!inAfter) return [{ path, kind: "REMOVED", before: from }]
    if (!inBefore) return [{ path, kind: "ADDED", after: to }]
    if (isPlainObject(from) && isPlainObject(to)) {
      return diffDocument(from, to, path)
    }
    if (JSON.stringify(from) === JSON.stringify(to)) return []
    return [{ path, kind: "CHANGED", before: from, after: to }]
  })
}

/** How many sampled documents each path changes in, most common first. */
export function summarizeChanges(
  perSample: ReadonlyArray<ReadonlyArray<FieldChange>>
): { path: string; kind: FieldChangeKind; count: number }[] {
  const byPath = new Map<
    string,
    { path: string; kind: FieldChangeKind; count: number }
  >()
  for (const changes of perSample) {
    for (const change of changes) {
      const entry = byPath.get(change.path)
      if (entry) {
        entry.count += 1
        if (entry.kind !== change.kind) entry.kind = "CHANGED"
      } else {
        byPath.set(change.path, {
          path: change.path,
          kind: change.kind,
          count: 1
        })
      }
    }
  }
  return Array.from(byPath.values()).sort((a, b) => b.count - a.count)
}

export function formatValue(value: unknown): string {
  if (value === undefined) return "—"
  return JSON.stringify(value)
}
